// Import jsPDF from our custom setup file
import {jsPDF} from './jspdf-setup'
import {format} from 'date-fns'

/**
 * Generate a PDF from reservation statement data
 *
 * @param {Object} options - PDF generation options
 * @param {Array} options.reservations - Array of reservation data
 * @param {String} options.currency - Currency code (default: 'EUR')
 * @param {String} options.title - PDF title
 * @param {String} options.period - Period string (e.g., 'January 2023')
 * @param {String} options.filename - Output filename (default: 'reservation-statement.pdf')
 * @returns {jsPDF} - The generated PDF document
 */
export const generateReservationStatementPDF = options => {
    const {
        reservations = [],
        currency = 'EUR',
        title = 'Reservation Statement',
        period = '',
        filename = 'reservation-statement.pdf',
    } = options

    if (!reservations || !Array.isArray(reservations) || reservations.length === 0) {
        throw new Error('No reservations data provided')
    }

    // Create a new PDF document
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
    })

    // Add a header bar with teal color
    doc.setFillColor(0, 128, 128) // Teal color
    doc.rect(0, 0, doc.internal.pageSize.width, 15, 'F')

    // Extract supplier name from title if present
    let headerText = 'Zero Stars Hotels Reservation Statements'

    // Check if title contains supplier name (format: "Monthly Invoice - Supplier Name")
    const titleParts = title.split(' - ')
    if (titleParts.length > 1) {
        // Add "for Supplier Name" to the header text
        headerText += ` for ${titleParts[1]}`
    }

    // Add header text
    doc.setTextColor(255, 255, 255) // White text
    doc.setFontSize(16)
    doc.text(headerText, 14, 10)

    // Reset text color to black for the rest of the document
    doc.setTextColor(0, 0, 0)

    // Add title
    doc.setFontSize(18)
    doc.text(title, 14, 22)

    // Add period if provided
    if (period) {
        doc.setFontSize(12)
        doc.text(`Period: ${period}`, 14, 30)
    }

    // Add generation date
    const currentDate = format(new Date(), 'yyyy-MM-dd HH:mm')
    doc.setFontSize(10)
    doc.text(`Generated: ${currentDate}`, 14, 36)

    // Format currency for display
    const formatCurrency = value => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(value)
    }

    // Format date for display
    const formatDate = date => {
        if (!date) return ''
        return format(new Date(date), 'yyyy-MM-dd')
    }

    // Format sustainability level for display
    const formatSustainabilityLevel = level => {
        if (!level) return 'Basic'
        switch (level.toUpperCase()) {
            case 'ZERO':
                return 'Zero'
            case 'SUSTAINABLE':
                return 'Sustainable'
            case 'BASIC':
            default:
                return 'Basic'
        }
    }

    // Prepare table data
    const tableData = reservations.map(item => [
        item.bookNumber,
        item.guestName,
        formatSustainabilityLevel(item.sustainabilityLevel),
        `${formatDate(item.checkIn)}\n${formatDate(item.checkOut)}`,
        item.nights,
        item.commissionPercentage,
        item.result,
        formatCurrency(item.originalAmount),
        formatCurrency(item.finalAmount),
        formatCurrency(item.paymentCharge),
        formatCurrency(item.commissionAmount),
    ])

    // Calculate totals
    const totalOriginalAmount = reservations.reduce((sum, item) => sum + item.originalAmount, 0)
    const totalFinalAmount = reservations.reduce((sum, item) => sum + item.finalAmount, 0)
    const totalPaymentCharge = reservations.reduce((sum, item) => sum + item.paymentCharge, 0)
    const totalCommissionAmount = reservations.reduce((sum, item) => sum + item.commissionAmount, 0)

    // Add totals row
    tableData.push([
        {content: 'Total:', colSpan: 7, styles: {fontStyle: 'bold', halign: 'right'}},
        {content: formatCurrency(totalOriginalAmount), styles: {fontStyle: 'bold', halign: 'right'}},
        {content: formatCurrency(totalFinalAmount), styles: {fontStyle: 'bold', halign: 'right'}},
        {content: formatCurrency(totalPaymentCharge), styles: {fontStyle: 'bold', halign: 'right'}},
        {content: formatCurrency(totalCommissionAmount), styles: {fontStyle: 'bold', halign: 'right'}},
    ])

    // Check if autoTable is available
    if (typeof doc.autoTable === 'function') {
        // Generate the table
        doc.autoTable({
            startY: 40,
            head: [
                [
                    'Book Number',
                    'Guest Name',
                    'Room Type',
                    'Check In/Out',
                    'Nights',
                    'Commission %',
                    'Result',
                    'Original Amount',
                    'Final Amount',
                    'Charge',
                    'Commission Amount',
                ],
            ],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: [0, 128, 128], // Teal color for header
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'center',
            },
            columnStyles: {
                0: {halign: 'left'}, // Book Number
                1: {halign: 'left'}, // Guest Name
                2: {halign: 'left'}, // Room Type
                3: {halign: 'left'}, // Check In/Out
                4: {halign: 'center'}, // Nights
                5: {halign: 'center'}, // Commission %
                6: {halign: 'left'}, // Result
                7: {halign: 'right'}, // Original Amount
                8: {halign: 'right'}, // Final Amount
                9: {halign: 'right'}, // Payment Charge
                10: {halign: 'right'}, // Commission Amount
            },
            styles: {
                overflow: 'linebreak',
                cellPadding: 3,
                fontSize: 9,
            },
            margin: {top: 40},
        })
    } else {
        console.error('autoTable is not available, falling back to basic table')

        // Fallback to basic table
        doc.setFontSize(12)
        doc.text('Reservation Data (autoTable not available)', 14, 50)

        // Draw a simple table manually
        let y = 60
        const lineHeight = 10

        // Headers
        doc.setFontSize(10)
        doc.setFont(undefined, 'bold')
        doc.text('Book Number', 14, y)
        doc.text('Guest Name', 50, y)
        doc.text('Room Type', 85, y)
        doc.text('Amount', 120, y)
        doc.text('Commission', 160, y)
        y += lineHeight

        // Data rows
        doc.setFont(undefined, 'normal')
        reservations.forEach(item => {
            doc.text(item.bookNumber.toString(), 14, y)
            doc.text(item.guestName, 50, y)
            doc.text(formatSustainabilityLevel(item.sustainabilityLevel), 85, y)
            doc.text(formatCurrency(item.originalAmount), 120, y)
            doc.text(formatCurrency(item.commissionAmount), 160, y)
            y += lineHeight
        })

        // Total row
        y += 5
        doc.setFont(undefined, 'bold')
        doc.text('Total:', 50, y)
        doc.text(formatCurrency(totalOriginalAmount), 120, y)
        doc.text(formatCurrency(totalCommissionAmount), 160, y)
    }

    // Save the PDF
    if (filename) {
        doc.save(filename)
    }

    return doc
}
